<template>
  <div
    class="wrapper"
    :style="style">
    <div class="content">
      <div class="form-wrapper">
        <v-window v-model="step">
          <v-window-item :value="1">
            <div class="flip-img-wrapper">
              <v-icon class="user-icon">
                fas fa-3x fa-user-lock
              </v-icon>
            </div>
            <div
              v-if="invalidToken || failedToReset"
              class="error-message">
              {{ $t('errors.resetPassword.invalidOrExpiredToken', 'Invalid or expired token') }}
            </div>
            <div
              class="message-reset"
              v-if="!invalidToken">
              {{ $t('errors.common.badPasswordPattern', 'Passwords must be at least eight ' +
                'characters including one in upper case and at least one number and special character') }}
            </div>
            <div
              class="message-reset"
              v-if="invalidToken">
              {{ $t('errors.resetPassword.resendLink', 'Your link has expired or invalid please request a new password change') }}
            </div>
            <br>
            <v-form
              ref="form"
              v-model="valid"
              v-if="!invalidToken"
              class="animate-form"
              @submit.prevent="onSubmit"
              lazy-validation>
              <v-text-field
                type="password"
                solo
                v-model="resetInfo.password"
                :rules="[rules.required, rules.passwordPattern]"
                :label="$t('views.resetPassword.fields.password', 'New Password')"
                append-icon="lock"
                required />
              <v-text-field
                type="password"
                solo
                v-model="resetInfo.confirmPassword"
                :rules="[rules.required, rules.matchPassword]"
                :label="$t('views.resetPassword.fields.confirmPassword', 'Confirm Password')"
                append-icon="lock"
                required />
              <button
                type="submit"
                class="btn submit-btn"
                :disabled="!valid || invalidToken">
                <span class="login-text">
                  {{ $t('views.resetPassword.actions.reset', 'Reset') }}
                </span>
              </button>
              <div class="links">
                <a
                  class="link"
                  @click="navigate('login')">
                  {{ $t('views.common.actions.connectWithExistingAccount', 'Connect with an existing account') }}
                </a>
              </div>
            </v-form>
            <div
              class="links"
              v-if="invalidToken">
              <a
                class="link"
                @click="navigate('login')">
                {{ $t('views.common.actions.connectWithExistingAccount', 'Connect with an existing account') }}
              </a>
            </div>
          </v-window-item>
          <v-window-item
            :value="2"
            style="Height:300px">
            <div class="message-reset">
              {{ $t('views.resetPassword.success', 'Your password has been changed') }}
            </div>
            <a
              class="link"
              @click="navigate('login')">
              {{ $t('views.common.actions.connectWithExistingAccount', 'Connect with an existing account') }}
            </a>
          </v-window-item>
        </v-window>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { rules } from '../services'
import Vue from 'vue'
import { VueReCaptcha } from 'vue-recaptcha-v3'

const defaultBackgroundImage = require('../../../assets/img/login-background.jpg')

Vue.use(VueReCaptcha, {
  siteKey: process.env.VUE_APP_RECAPATCHA,
  loaderOptions: {
    useRecaptchaNet: false
  }
})

export default {
  data () {
    return {
      failedToReset: false,
      invalidToken: false,
      step: 1,
      valid: true,
      resetInfo: {
        instance: null,
        password: null,
        confirmPassword: null,
        captchaToken: null
      },
      rules: {
        ...rules,
        recovery: () => !this.failedToRecovery || this.$t('errors.common.fail', 'An error occurred'),
        matchPassword: value => value === this.resetInfo.password || this.$t('errors.common.doesNotMatch', 'The confirm password does not match.')
      }
    }
  },
  computed: {
    ...mapState({
      authentication: state => state.authentication
    }),
    backgroundImage () {
      return this.authentication.config.backgroundImage || defaultBackgroundImage
    },
    style () {
      return {
        'background-image': `url(${this.backgroundImage})`
      }
    },
    instance: {
      get () {
        return this.resetInfo.instance
      },
      set (value) {
        this.resetInfo.instance = value
      }
    }
  },
  methods: {
    ...mapActions('authentication', [
      'resetPassword',
      'checkResetToken',
      'loadConfig'
    ]),
    validate () {
      return this.$refs.form.validate()
    },
    resetValidation () {
      this.failedToReset = false
      this.validate()
    },
    navigate (name) {
      this.$router.push(name)
    },
    handleResponseError ({ error }) {
      if (!error || !error.response) {
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.common.fatal', `Fatal error. Please contact administrator`))
      } else if (error.response.status === 400 && error.response.data.i18nKey === 'INVALID_PASSWORD_RECOVERY_TOKEN_ERROR') {
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.common.invalidToken', `Token is invalid`))
      } else if (error.response.status === 400 && error.response.data.i18nKey === 'EXPIRED_PASSWORD_RECOVERY_TOKEN_ERROR') {
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.common.expiredToken', `Token expired`))
      } else if (error.response.status === 400 && error.response.data.i18nKey === 'PASSWORD_COMPLEXITY_ERROR') {
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.common.badPasswordPattern', `the password does not respect the complexity`))
      } else if (error.response.status === 400 && error.response.data.i18nKey === 'INVALID_PARAMETER') {
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.common.emptyPassword', `Password is null or empty`))
      } else if (error.response.status === 400 && error.response.data.i18nKey === 'PASSWORD_NOT_EQUALS_ERROR') {
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.common.doesNotMatch', `The confirm password does not match`))
      } else {
        const message = (error && error.response && error.response.data && error.response.data.message) ? error.response.data.message : 'unknown'
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.common.generic', { message: message }, `An error occurred : ${message}`))
      }
      throw error
    },
    async onSubmit () {
      if (this.validate()) {
        try {
          await this.$recaptchaLoaded()
          // Execute reCAPTCHA with action "login".
          const token = await this.$recaptcha('login')
          this.resetInfo.captchaToken = token
          await this.resetPassword(this.resetInfo)
          this.step++
        } catch (error) {
          this.handleResponseError({
            error
          })
          this.failedToReset = true
          this.validate()
        }
      }
    }
  },
  watch: {
    '$route': {
      async handler (to, from) {
        this.loadConfig(to.query.portal)
        if (to.query.instance) {
          this.resetInfo.instance = to.query.instance
        }
        if (to.query.token) {
          this.resetInfo.token = to.query.token
          try {
            await this.checkResetToken(this.resetInfo)
            this.invalidToken = false
          } catch (e) {
            this.invalidToken = true
          }
        } else {
          this.invalidToken = true
        }
      },
      immediate: true
    },
    resetInfo: {
      handler () {
        this.resetValidation()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss">
  .grecaptcha-badge {
    z-index: 99;
  }
</style>

<style lang="scss" scoped>
  @import "../assets/login";

  .error-message {
    color: red;
    font-size: 16px;
    text-align: center;
    margin-bottom: 5px;
  }

  .label-reset, .message-reset {
    color: white;
  }

  .label-reset::after {
    content: "*";
    color: white;
  }

  .text--black {
    /deep/ {
      input, .v-icon {
        color: black !important;
      }
    }
  }
</style>
